import type { Database } from "@fieldbook/common/types/database"

type TableName = keyof Database["public"]["Tables"]

/**
 * Columns through which an uploaded row references a parent record, and the
 * table that parent lives in.
 */
export const PARENT_REFERENCE_COLUMNS = {
  observation_id: "observations",
  survey_site_id: "survey_sites",
  geospatial_data_id: "geospatial_data",
  collection_id: "collections",
  species_id: "species",
  photo_id: "photos",
  planned_site_id: "planned_sites",
} satisfies Record<string, TableName>

export type ParentTable =
  (typeof PARENT_REFERENCE_COLUMNS)[keyof typeof PARENT_REFERENCE_COLUMNS]

type RemapReader = {
  getAll<T>(sql: string, parameters?: unknown[]): Promise<T[]>
}

type RemapWriter = RemapReader & {
  execute(sql: string, parameters?: unknown[]): Promise<unknown>
}

type IdRemapRow = {
  target_table: string
  local_id: string
  server_id: string
}

const remapKey = (table: string, id: string) => `${table}:${id}`

/**
 * Ids that were remapped during upload, keyed `table:localId`.
 *
 * A local row can turn out to duplicate one the server already has (e.g. a
 * species with the same name created by another device). The uploader then
 * deletes the local row and points local references at the server's id. That
 * remap is recorded here because rows parked behind the parent were queued
 * with the old id, and may be restored — and uploaded — after the remap has
 * already run.
 */
export async function loadIdRemaps(
  database: RemapReader,
): Promise<Map<string, string>> {
  try {
    const rows = await database.getAll<IdRemapRow>(
      "SELECT target_table, local_id, server_id FROM sync_id_remaps",
    )
    return new Map(
      rows.map((row) => [
        remapKey(row.target_table, row.local_id),
        row.server_id,
      ]),
    )
  } catch (error) {
    // A remap only saves a failed upload that the normal retry path would
    // eventually hit an FK violation on; it must never fail the upload.
    console.warn("[PowerSync] Could not load id remaps:", error)
    return new Map()
  }
}

/**
 * Return `data` with every parent reference that a remap superseded pointing
 * at the canonical row instead. `data` is left untouched when nothing
 * applies, so callers can keep sharing the queued op data.
 */
export function applyIdRemaps(
  data: Record<string, unknown>,
  remaps: ReadonlyMap<string, string>,
): Record<string, unknown> {
  if (remaps.size === 0) return data

  let result = data
  for (const [column, table] of Object.entries(PARENT_REFERENCE_COLUMNS)) {
    const id = data[column]
    if (typeof id !== "string") continue
    const remapped = remaps.get(remapKey(table, id))
    if (remapped === undefined || remapped === id) continue
    if (result === data) result = { ...data }
    result[column] = remapped
  }
  return result
}

/**
 * Record that local row `localId` was replaced by server row `serverId`.
 * Written in the same transaction as the remap itself, so a parked child
 * restored afterwards can resolve the reference even if the local row is
 * already gone.
 */
export async function recordIdRemap(
  tx: RemapWriter,
  {
    table,
    localId,
    serverId,
  }: { table: string; localId: string; serverId: string },
): Promise<void> {
  // One canonical mapping per local row: the latest remap wins.
  await tx.execute(
    "DELETE FROM sync_id_remaps WHERE target_table = ? AND local_id = ?",
    [table, localId],
  )
  await tx.execute(
    `INSERT INTO sync_id_remaps (id, target_table, local_id, server_id, created_at)
     VALUES (?, ?, ?, ?, ?)`,
    [crypto.randomUUID(), table, localId, serverId, new Date().toISOString()],
  )
}
